import { useMemo } from 'react';
import {
  type SmartZoneField,
  type SmartZoneFormData,
  type UpdateRecords,
} from '../../../../javascripts/shared-types';
import terminology from '../../../../javascripts/terminology';
import { type Workflow } from '../../../framework/models';
import { optionResolverFactory } from './optionResolver';
import { getProjectQuery, getTeamsQuery } from './queries';
import { getRecordUpdater } from './updateRecords';

type Project = ReturnType<typeof getProjectQuery>;
type Teams = ReturnType<typeof getTeamsQuery>;
type WorkflowName =
  | 'ideaWorkflow'
  | 'epicWorkflow'
  | 'featureWorkflow'
  | 'requirementWorkflow'
  | 'initiativeWorkflow'
  | 'strategicImperativeWorkflow'
  | 'releaseWorkflow'
  | 'ideaWorkflow';

export type GetWorkflow = (name: WorkflowName) => Promise<Workflow | null>;

interface SmartZoneInfo {
  formData: SmartZoneFormData;
  updateRecords: UpdateRecords;
}

interface SmartZonePermissions {
  showStrategy: boolean;
  showRoadmaps: boolean;
  showTeams: boolean;
  showIdeas: boolean;
}

const getSmartZoneInfo = (
  projectId: RecordId,
  permissions: SmartZonePermissions
): SmartZoneInfo => {
  let projectPromise: Awaited<Project> | Project | undefined;
  const getProject = async () => {
    projectPromise ??= getProjectQuery(projectId);
    const resolved = await projectPromise;
    return resolved;
  };

  let teamsPromise: Awaited<Teams> | Teams | undefined;
  const getTeams = async () => {
    teamsPromise ??= getTeamsQuery();
    const resolved = await teamsPromise;
    return resolved;
  };

  const getWorkflow: GetWorkflow = async name => {
    const project = await getProject();
    return project[name];
  };

  const getWorkflowStatuses = (workflow: Workflow) =>
    workflow?.workflowStatuses?.map(status => ({
      value: status.id,
      text: status.name,
    })) ?? [];

  const getTeamOptions = async () => {
    const teams = await getTeams();
    return teams?.map(status => ({
      value: status.id,
      text: status.name,
    }));
  };

  const getTeamWorkflow: GetWorkflow = async name => {
    const project = await getProject();

    // use the first associated team if possible...
    let workflow = project.developTeams?.[0]?.[name];

    // ...falling back to the first account team
    if (!workflow) {
      const teams = await getTeams();
      workflow = teams?.[0]?.[name];
    }

    return workflow;
  };

  const getTeamWorkflowStatuses = async (
    workflowName: 'featureWorkflow' | 'epicWorkflow',
    teamId: unknown
  ) => {
    let workflow: Workflow | undefined | null;
    if (teamId) {
      const teams = await getTeams();
      const team = teams?.find(t => t.id === teamId);
      workflow = team?.[workflowName];
    } else {
      workflow = await getTeamWorkflow(workflowName);
    }
    return workflow ? getWorkflowStatuses(workflow) : [];
  };

  const team: SmartZoneField = {
    name: 'team',
    description: 'Team',
    fieldType: 'select',
    fetch: getTeamOptions,
  };

  const assignedToUser: SmartZoneField = {
    name: 'assignedToUser',
    description: 'Assigned to',
    fieldType: 'select',
    fetchUrl: '/api/v1/users/picker',
    resolveOptions: optionResolverFactory('users'),
  };

  const goals: SmartZoneField = {
    name: 'addGoals',
    description: terminology('Goals'),
    fieldType: 'multi-select',
    fetchUrl: '/api/v1/goals/picker',
    resolveOptions: optionResolverFactory('goals'),
  };

  const initiative: SmartZoneField = {
    name: 'initiative',
    description: terminology('Initiative'),
    fieldType: 'select',
    fetchUrl: '/api/v1/initiatives/picker',
    resolveOptions: optionResolverFactory('initiatives'),
  };

  const epic: SmartZoneField = {
    name: 'epic',
    description: terminology('Epic'),
    fieldType: 'select',
    fetchUrl: '/api/v1/epics/picker',
    resolveOptions: optionResolverFactory('epics'),
  };

  const release: SmartZoneField = {
    name: 'release',
    description: terminology('Release'),
    fieldType: 'select',
    fetchUrl: '/api/v1/releases/picker',
    resolveOptions: optionResolverFactory('releases'),
  };

  const workspace: SmartZoneField = {
    name: 'project',
    description: terminology('Workspace'),
    fieldType: 'select',
    fetchUrl: '/api/v1/products/picker?nested_teams=true',
    resolveOptions: optionResolverFactory('projects'),
  };

  const ideaCategories: SmartZoneField = {
    name: 'addIdeaCategories',
    fetchUrl: `/ideas/idea_categories?project_id=${projectId}`,
    description: 'Categories',
    fieldType: 'multi-select',
    resolveOptions: optionResolverFactory('idea_categories'),
    branchesSelectable: true,
  };

  const ideaThemes: SmartZoneField = {
    name: 'addIdeaThemes',
    description: 'Themes',
    fieldType: 'multi-select',
    fetchUrl: '/api/v1/idea_themes/picker',
    resolveOptions: optionResolverFactory('idea_themes'),
  };

  // TODO need to add this to backend
  // const timeframe = {
  //   name: 'epoch',
  //   description: terminology('Timeframe'),
  //   fieldType: 'select',
  //   fetchUrl: '/api/v1/epochs/picker',
  // };

  const { showRoadmaps, showStrategy, showIdeas, showTeams } = permissions;

  const ideasFields: SmartZoneFormData = [
    {
      recordType: 'ideas',
      name: terminology('Ideas'),
      fields: [
        {
          name: 'workflowStatus',
          description: 'Status',
          fieldType: 'select',
          fetch: () => getWorkflow('ideaWorkflow').then(getWorkflowStatuses),
        },
        workspace,
        assignedToUser,
        ideaCategories,
        ideaThemes,
      ],
    },
  ];
  const roadmapsFields: SmartZoneFormData = [
    {
      recordType: 'features',
      name: terminology('Features'),
      fields: [
        {
          name: 'workflowStatus',
          description: 'Status',
          fieldType: 'select',
          fetch: () => getWorkflow('featureWorkflow').then(getWorkflowStatuses),
        },
        release,
        ...(showTeams
          ? [
              team,
              {
                name: 'teamWorkflowStatus',
                description: 'Team status',
                fieldType: 'select',
                fetch: async formState =>
                  getTeamWorkflowStatuses('featureWorkflow', formState?.team),
                getKey: formState => String(formState?.team),
              } as SmartZoneField,
            ]
          : []),
        assignedToUser,
        epic,
        ...(showStrategy ? [goals, initiative] : []),
      ],
    },
    {
      recordType: 'requirements',
      name: terminology('Requirements'),
      fields: [
        {
          name: 'workflowStatus',
          description: 'Status',
          fieldType: 'select',
          fetch: () =>
            getWorkflow('requirementWorkflow').then(getWorkflowStatuses),
        },
        assignedToUser,
      ],
    },
    {
      recordType: 'epics',
      name: terminology('Epics'),
      fields: [
        {
          name: 'workflowStatus',
          description: 'Status',
          fieldType: 'select',
          fetch: () => getWorkflow('epicWorkflow').then(getWorkflowStatuses),
        },
        ...(showTeams
          ? [
              team,
              {
                name: 'teamWorkflowStatus',
                description: 'Team status',
                fieldType: 'select',
                fetch: async formState =>
                  getTeamWorkflowStatuses('epicWorkflow', formState?.team),
                getKey: formState => String(formState?.team),
              } as SmartZoneField,
            ]
          : []),
        release,
        assignedToUser,
        ...(showStrategy ? [goals, initiative] : []),
      ],
    },
    {
      recordType: 'releases',
      name: terminology('Releases'),
      fields: [
        {
          name: 'workflowStatus',
          description: 'Status',
          fieldType: 'select',
          fetch: () => getWorkflow('releaseWorkflow').then(getWorkflowStatuses),
        },
        assignedToUser,
        workspace,
        ...(showStrategy
          ? [
              goals,
              {
                name: 'addInitiatives',
                description: terminology('Initiatives'),
                fieldType: 'multi-select',
                fetchUrl: '/api/v1/initiatives/picker',
                resolveOptions: optionResolverFactory('initiatives'),
              } as SmartZoneField,
            ]
          : []),
      ],
    },
    ...(showStrategy
      ? [
          {
            recordType: 'goals',
            name: 'Goals',
            fields: [
              {
                name: 'workflowStatus',
                description: 'Status',
                fieldType: 'select',
                fetch: () =>
                  getWorkflow('strategicImperativeWorkflow').then(
                    getWorkflowStatuses
                  ),
              } as SmartZoneField,
              workspace,
              // timeframe,
            ],
          },
        ]
      : []),
    {
      recordType: 'initiatives',
      name: terminology('Initiatives'),
      fields: [
        {
          name: 'workflowStatus',
          description: 'Status',
          fieldType: 'select',
          fetch: () =>
            getWorkflow('initiativeWorkflow').then(getWorkflowStatuses),
        },
        workspace,
        assignedToUser,
        // timeframe,
        ...(showStrategy ? [goals] : []),
      ],
    },
  ];

  const formData: SmartZoneFormData = [
    ...(showRoadmaps ? roadmapsFields : []),
    ...(showIdeas ? ideasFields : []),
  ];

  return {
    updateRecords: async updates => {
      return getRecordUpdater(formData, {
        getWorkflow,
        getTeamWorkflow,
      })(updates);
    },
    formData,
  };
};

export const useSmartZoneInfo = (projectId: RecordId | undefined) => {
  const {
    roadmapFlavor = false,
    ideasFlavor = false,
    teamworkOrDevelopFlavor = false,
  } = window.currentAccount ?? {};

  const smartZoneInfo = useMemo(() => {
    const canAccessSmartZones =
      roadmapFlavor || ideasFlavor || teamworkOrDevelopFlavor;

    return canAccessSmartZones && projectId
      ? getSmartZoneInfo(projectId, {
          showRoadmaps: roadmapFlavor || teamworkOrDevelopFlavor,
          showStrategy: roadmapFlavor,
          showIdeas: roadmapFlavor || ideasFlavor,
          showTeams: teamworkOrDevelopFlavor,
        })
      : undefined;
  }, [projectId, roadmapFlavor, ideasFlavor, teamworkOrDevelopFlavor]);

  return smartZoneInfo;
};
